<template>
  <div class="home-wrapper">
    <!-- 💎 Welcome Section -->
    <div class="welcome-card" role="main" aria-labelledby="welcome-title">
      <!-- Main Headline -->
      <h1 id="welcome-title" class="title">
        Write Stories. Publish Freely. Earn Rewards.
      </h1>

      <!-- Sub Headline -->
      <p class="subtitle">
        A creative platform where writers share poetry and stories, and readers discover them. 
        Star ratings are automatically calculated based on likes and comments — helping engaging content rise naturally.
      </p>

      <!-- Bonus Line -->
      <p v-if="!isLoggedIn" class="bonus" aria-live="polite">
        Get <strong style="color:#00eaff">10 Mudra Tokens</strong> instantly when you create your free account.
      </p>

      <!-- How Rewards Actually Work -->
      <div v-if="!isLoggedIn" class="rewards-info">
        <h3>How You Earn Mudra Tokens</h3>
        <ul>
          <li>Awarded automatically when you publish a story (for your creative contribution)</li>
          <li>No tokens from likes, comments, stars, views, or any engagement — stars are auto-calculated from likes & comments for visibility only</li>
          <li>Occasional contests with prize tokens for winners (announced here or on X/Telegram)</li>
        </ul>
      </div>

      <!-- CTA Button -->
      <button v-if="!isLoggedIn" class="start-btn" @click="goToRegister">
        Start Writing
      </button>

      <!-- Logged-in Message -->
      <p v-else class="welcome-back">
        Welcome back! Share your next story and publish to earn.
      </p>

      <p v-if="isLoggedIn" class="referral-line">
         Share your referral link and earn 
            <strong style="color:#00eaff">2 Mudra Tokens</strong> 
            for every signup.
      </p>

      <!-- Trust / Disclaimer Line -->
      <p v-if="!isLoggedIn" class="trust-line">
        Free to join • No investment required • Built for writers
      </p>
      <p class="disclaimer-small">
        Mudra Tokens are utility rewards for publishing — not financial assets or investments.
      </p>
    </div>

    <!-- 🏆 Ongoing Contests Section -->
    <div v-if="contests.length > 0" class="contest-layout">
      <h2 class="contest-heading">Ongoing Contests</h2>

      <div class="contest-grid">
        <div
          v-for="contest in contests.slice(0, 3)"
          :key="contest.id"
          class="contest-card"
          @click="goToContest(contest.id)"
        >
          <h3>{{ contest.title }}</h3>
          <span class="theme">{{ contest.theme }}</span>
          <p class="desc">{{ contest.description }}</p>
          <p class="reward">💠 Reward: {{ contest.reward }}</p>
          <p class="date">📅 {{ formatDate(contest.start_date) }} → {{ formatDate(contest.end_date) }}</p>
        </div>
      </div>
    </div>

    <!-- ⭐ For Readers Section -->
    <div class="readers-section">
      <h2 class="readers-heading">For Readers: Discover & Engage</h2>

      <p class="readers-subtitle">
        Discover powerful poetry, compelling short stories, and original creative writing crafted by writers from across the globe.
      </p>

      <div class="reader-features">
        <div class="feature">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#ffd700" stroke-width="1.8" stroke-linejoin="round" fill="rgba(255,215,0,0.12)"/>
            </svg>
          </div>
          <h3>Engagement-Driven Visibility</h3>
          <p>Stories gain higher star ratings automatically based on likes and comments from readers — helping the most appreciated content rise naturally.</p>
        </div>

        <div class="feature">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="#ff6b9d" stroke-width="1.8" stroke-linejoin="round" fill="rgba(255,107,157,0.12)"/>
            </svg>
          </div>
          <h3>Like & Comment to Participate</h3>
          <p>Show support with likes and share your thoughts in comments. These interactions automatically influence the star rating — no manual star selection, no tokens awarded for engaging.</p>
        </div>

        <div class="feature">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" stroke="#00c8ff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" stroke="#00c8ff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4 22h16" stroke="#00c8ff" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" stroke="#00c8ff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" stroke="#00c8ff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" stroke="#00c8ff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="rgba(0,200,255,0.1)"/>
            </svg>
          </div>
          <h3>Help Shape the Community</h3>
          <p>Active readers who like and comment thoughtfully may be featured, get shoutouts, or qualify for future reader-focused contests.</p>
        </div>
      </div>

      <p class="reader-note">
        Star ratings are auto-generated from likes and comments for discovery purposes only. 
        Mudra Tokens are awarded solely for publishing stories or winning contests.
      </p>

      <button class="browse-btn" @click="goToBrowse" v-if="isLoggedIn">
        Browse Stories Now →
      </button>
    </div>

    <!-- ✅ Bottom Ad Banner -->
    <div class="ad-section">
      <AdBanner position="bottom" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { supabase } from "@/supabase";
import AdBanner from "@/components/AdBanner.vue";

const router = useRouter();
const isLoggedIn = ref(false);
const contests = ref([]);

onMounted(async () => {
  const { data } = await supabase.auth.getSession();
  isLoggedIn.value = !!data?.session;
  await loadContests();
});

async function loadContests() {
  const today = new Date().toISOString().split("T")[0];
  const { data, error } = await supabase
    .from("contests")
    .select("*")
    .eq("active", true)
    .gte("end_date", today)
    .order("start_date", { ascending: false });

  if (error) console.error("Error fetching contests:", error);
  else contests.value = data || [];
}

function goToContest(id) {
  router.push(`/contest/${id}`);
}
function goToRegister() {
  router.push("/register");
}
function goToBrowse() {
  router.push("/stories"); // adjust this to your actual stories/browse route if different
}
function formatDate(date) {
  return new Date(date).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap");

.home-wrapper {
  min-height: 100vh;
  width: 100%;
  background-image: url("@/assets/mudra-bg.png");
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  position: relative;
  padding-top: 80px;
  color: #eaf6ff;
  font-family: "Inter", sans-serif;
}
.home-wrapper::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.9));
  z-index: 0;
}

/* Welcome Card */
.welcome-card {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 680px;
  margin: 0 auto 60px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 45px 40px;
  border: 1px solid rgba(0, 200, 255, 0.25);
  box-shadow: 0 8px 40px rgba(0, 200, 255, 0.15);
}
.title { font-size: 40px; font-weight: 700; color: #00eaff; margin-bottom: 12px; }
.subtitle { font-size: 17px; margin-bottom: 20px; line-height: 1.5; }
.bonus { font-size: 15px; margin-bottom: 15px; }
.rewards-info {
  background: rgba(0, 200, 255, 0.08);
  border-radius: 10px;
  padding: 16px 24px;
  margin: 20px 0;
  text-align: left;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}
.rewards-info h3 { font-size: 18px; color: #00eaff; margin-bottom: 10px; }
.rewards-info ul { list-style: none; padding-left: 0; margin: 0; }
.rewards-info li { margin: 8px 0; font-size: 14px; position: relative; padding-left: 24px; }
.rewards-info li::before { content: "•"; position: absolute; left: 0; color: #00ffd0; font-size: 20px; }
.start-btn {
  background: linear-gradient(90deg, #00c8ff, #007bff);
  color: #000;
  padding: 12px 28px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  border: none;
  transition: 0.3s ease;
}
.start-btn:hover { transform: scale(1.05); background: linear-gradient(90deg, #00eaff, #0099ff); }
.welcome-back { font-size: 16px; color: #aeefff; }
.trust-line { font-size: 14px; margin-top: 20px; opacity: 0.9; }
.disclaimer-small { font-size: 13px; margin-top: 12px; opacity: 0.75; font-style: italic; }

/* Contest Layout */
.contest-layout { position: relative; z-index: 2; padding: 20px; max-width: 1200px; margin: 0 auto 60px; text-align: center; }
.contest-heading { font-size: 26px; color: #00eaff; margin-bottom: 20px; text-shadow: 0 0 12px rgba(0, 234, 255, 0.6); }
.contest-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 25px; justify-items: center; align-items: start; }
.contest-card {
  background: rgba(0, 30, 60, 0.8);
  border-radius: 14px;
  padding: 20px;
  border: 1px solid rgba(0, 200, 255, 0.25);
  box-shadow: 0 0 20px rgba(0, 200, 255, 0.1);
  width: 100%;
  max-width: 340px;
  transition: 0.3s ease;
  cursor: pointer;
  text-align: left;
}
.contest-card:hover { transform: scale(1.05); box-shadow: 0 0 25px rgba(0, 234, 255, 0.4); }
.contest-card h3 { font-size: 18px; color: #00eaff; }
.theme { background: rgba(0, 200, 255, 0.15); padding: 4px 8px; border-radius: 8px; color: #aee0ff; font-size: 12px; }
.desc { font-size: 14px; color: #cdeaff; margin: 6px 0; }
.reward { color: #00ffd0; font-weight: 600; }
.date { font-size: 12px; color: #bdeaff; }

/* Readers Section */
.readers-section {
  position: relative;
  z-index: 2;
  padding: 48px 32px;
  max-width: 1100px;
  margin: 0 auto 60px;
  text-align: center;
  background: rgba(5, 14, 30, 0.6);
  border-radius: 20px;
  border: 1px solid rgba(0, 200, 255, 0.15);
  box-shadow: 0 0 40px rgba(0, 200, 255, 0.06);
  backdrop-filter: blur(10px);
}
.readers-heading {
  font-family: 'Orbitron', sans-serif;
  font-size: 24px;
  color: #ffd700;
  margin-bottom: 14px;
  text-shadow: 0 0 16px rgba(255, 215, 0, 0.4);
  letter-spacing: 0.5px;
}
.readers-subtitle {
  font-size: 16px;
  margin-bottom: 36px;
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
  color: #b8d8f0;
  line-height: 1.7;
}
.reader-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 36px;
}
.feature {
  background: rgba(0, 200, 255, 0.05);
  border-radius: 16px;
  padding: 28px 20px 24px;
  border: 1px solid rgba(0, 200, 255, 0.12);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.feature::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0, 200, 255, 0.03), transparent);
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.feature:hover {
  background: rgba(0, 200, 255, 0.1);
  border-color: rgba(0, 200, 255, 0.35);
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 200, 255, 0.15);
}
.feature:hover::before { opacity: 1; }
.feature-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}
.feature-icon svg {
  width: 40px;
  height: 40px;
  filter: drop-shadow(0 0 8px rgba(0, 200, 255, 0.4));
  transition: filter 0.3s ease, transform 0.3s ease;
}
.feature:hover .feature-icon svg {
  filter: drop-shadow(0 0 14px rgba(0, 200, 255, 0.7));
  transform: scale(1.15);
}
.feature h3 {
  font-size: 16px;
  color: #00ffd0;
  margin-bottom: 10px;
  font-weight: 700;
}
.feature p {
  font-size: 14px;
  color: #b8d8f0;
  line-height: 1.6;
}
.reader-note {
  font-size: 14px;
  color: #a0d0ff;
  font-style: italic;
  max-width: 760px;
  margin: 0 auto 24px;
}
.browse-btn {
  background: linear-gradient(90deg, #00ffd0, #00c8ff);
  color: #000;
  padding: 12px 32px;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: 0.3s ease;
}
.browse-btn:hover {
  transform: scale(1.05);
  background: linear-gradient(90deg, #00eaff, #00ffd0);
}

/* Bottom Ad */
.ad-section {
  position: relative;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.referral-line {
  margin-top: 8px;
  font-size: 14px;
  color: #bbb;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.2);
  padding: 8px 12px;
  border-radius: 8px;
  display: inline-block;
}

/* Responsive */
@media (max-width: 1000px) {
  .contest-grid, .reader-features {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  .welcome-card { padding: 35px 25px; }
  .title { font-size: 34px; }
}
</style>